/**
 * 값에서 배열을 추출 (객체의 배열 속성도 지원)
 * @param value - 배열 또는 객체
 * @returns {any[]} 추출된 배열
 */
export const getArrayFromValue = (value: any): any[] => {
  if (Array.isArray(value)) {
    return value;
  }
  if (value && typeof value === 'object') {
    if (value.agentNames && Array.isArray(value.agentNames)) {
      return value.agentNames;
    }
    let result: any[] = [];
    Object.keys(value).some((key) => {
      if (Array.isArray(value[key])) {
        result = value[key];
        return true;
      }
      return false;
    });
    return result;
  }
  return [];
};

/**
 * IP 주소 문자열 비교 (숫자 기반)
 * @param ipA - 첫 번째 IP
 * @param ipB - 두 번째 IP
 * @returns {number|null} 비교 결과
 */
export const compareIPAddresses = (ipA: string, ipB: string): number | null => {
  const ipPattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  const matchA = ipA.match(ipPattern);
  const matchB = ipB.match(ipPattern);
  if (matchA && matchB) {
    for (let i = 1; i <= 4; i++) {
      const numA = parseInt(matchA[i], 10);
      const numB = parseInt(matchB[i], 10);
      if (numA !== numB) {
        return numA - numB;
      }
    }
    return 0;
  }
  return null;
};

/**
 * 배열을 문자열로 변환 (소문자)
 * @param arr - 배열
 * @returns {string} 변환된 문자열
 */
export const arrayToString = (arr: any[]): string => {
  return arr
    .map((item) => String(item || ''))
    .join('')
    .toLowerCase();
};

/**
 * 배열 필드 정렬을 위한 유틸리티 함수
 * @param valueA - 비교할 첫번째 값 (배열 또는 문자열)
 * @param valueB - 비교할 두번째 값 (배열 또는 문자열)
 * @returns 정렬 비교 결과 (-1, 0, 1)
 */
export const compareArrayFieldValues = (valueA: any, valueB: any): number => {
  if (valueA === null || valueA === undefined) {
    return -1;
  }
  if (valueB === null || valueB === undefined) {
    return 1;
  }

  // 둘 다 문자열인 경우 직접 비교
  if (typeof valueA === 'string' && typeof valueB === 'string') {
    return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
  }

  // 기존 배열 처리 로직
  const arrayA = getArrayFromValue(valueA);
  const arrayB = getArrayFromValue(valueB);
  if (arrayA.length === 1 && arrayB.length === 1) {
    const ipA = String(arrayA[0] || '');
    const ipB = String(arrayB[0] || '');
    const ipCompareResult = compareIPAddresses(ipA, ipB);
    if (ipCompareResult !== null) {
      return ipCompareResult;
    }
  }
  const strA = arrayToString(arrayA);
  const strB = arrayToString(arrayB);
  return strA.localeCompare(strB);
};

/**
 * 두 배열이 중복 없이 동일한 값으로 구성되어 있는지 확인
 * @param arr1 - 첫 번째 배열
 * @param arr2 - 두 번째 배열
 * @returns {boolean}
 */
export const areArraysEqualUnorderedNoneDuplicated = <T>(arr1: T[], arr2: T[]): boolean => {
  if (arr1.length !== arr2.length) return false;
  const setA = new Set(arr1);
  const setB = new Set(arr2);
  return setA.size === setB.size && [...setA].every((item) => setB.has(item));
};

/**
 * 객체 배열을 키 기준으로 정렬
 * @param array 정렬할 배열
 * @param key 정렬 기준 키
 * @param sortType 정렬 방식(ascending/descending)
 * @returns 정렬된 배열
 */
export const sortObjectByKey = <T extends object>(
  array: T[],
  key: string,
  sortType: 'ascending' | 'descending' = 'ascending',
): T[] => {
  const arr = [...array]; // 원본 배열 복사
  if (sortType === 'ascending') {
    return arr.sort((a, b) => a[key] - b[key]);
  }
  return arr.sort((a, b) => b[key] - a[key]);
};

/**
 * 배열에 All sentinel 항목이 있는지 확인 (빌더가 심는 isAllItem 플래그로 식별)
 * displayName/name 접두사 기반 판별은 실데이터 이름과 충돌해 폐지 (DSP-44193)
 * @param arr 배열
 * @returns All sentinel 항목이 있으면 true
 */
export const isAllDisplayNameCheck = (arr: { isAllItem?: boolean }[]): boolean => {
  return arr.some((values) => !!values.isAllItem);
};
